import React, { useState, useEffect } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { AvatarColorName } from '../../types/AvatarColorName';
import { GroupByType } from '../EnquirySummaryRepository';
import { UserAutocompleteSelector } from '../../components/UserAutocompleteSelector';
import useOnClickOutside from 'use-onclickoutside';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import { Avatar, Chip } from '@material-ui/core';
import moment from 'moment';
import { AreaOfLawSelector } from '../selectors/AreaOfLawSelector';
// import MoreHorizOutlinedIcon from '@material-ui/icons/MoreHorizOutlined'; 
// import { UserFeature, UserFeatureEnum } from '../../types/UserFeature';
import { EnquirySummary } from '../models/Enquiry';
// import { useHistory } from 'react-router-dom';
// import { EnquirySummaryContext } from '../EnquirySummaryContext';
import { EnquiryTaskAction, EnquiryTaskActionHandle } from '../EnquiryTaskAction';
import { Rating } from '@material-ui/lab';
import { formatNumber } from '../../helpers/ChartHelper';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      userSelect: 'none',
      padding: 8,
      margin: '0 0 8px 0',
      color: '#000',
      display: 'flex',
      position: 'relative',
      backgroundColor: '#fff',
      borderRadius: '4px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
    },
    option: {
      display: 'flex',
      flex: 1,
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: '2px',
    },
    optionContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      margin: '-8px -8px 8px -8px',
      padding: '6px 8px',
      backgroundColor: '#f5f7fa',
      borderBottom: '1px solid #e8eaed',
      borderRadius: '4px 4px 0 0',
    },
    chipRow: {
      display: 'flex',
      flex: 1,
      alignItems: 'center',
      gap: '8px',
    },
    cardTypeLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '13px',
      fontWeight: 500,
      color: '#546e7a',
      '& svg': {
        fontSize: '17px',
        color: '#546e7a',
      },
    },
    cardSubLabel: {
      fontStyle: 'italic',
      color: '#989393',
      fontSize: '12px',
    },
    contentContainer: {
      // padding: '0 10px',
      display: 'flex',
      flexFlow: 'column',
      flex: 1,
    },
    content: {
      paddingBottom: '10px',
      flex: 1,
      '& .org': {
        fontWeight: 600,
        color: '#6d6c6c'
      }
    },
    otherDetails: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '12px',
      '& > div': {
        flex: 1,
      },
      '& .session': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      },
      '& .user': {
        backgroundColor: 'yellow',
        padding: '3px 5px',
        textTransform: 'capitalize',
        borderRadius: '3px',
      }
    },
    userContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '12px',
        padding: '10px 0',
        cursor: 'pointer',
        '& .name': {
          fontWeight: 600,
        },
        '& .category': {
          padding: '0 5px',
          fontWeight: 600,
        }
    },
    userSelector: {
      display: 'flex',
      alignItems: 'center',
      padding: '0 5px',
      margin: '0 -5px',
      '& > div': {
        padding: '0 5px',
      }
    },
    avatar: {
      width: '20px',
      height: '20px',
      fontSize: '10px',
    },
    areaOfLaw: {
      cursor: 'pointer'
    },
    status: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      cursor: 'pointer',
    },
    textField: {
      width: '100%',
      minWidth: '150px',
    }
  })
);

export const Colors: AvatarColorName[] = ['red', 'orange', 'olive', 'green', 'teal', 'blue',
    'purple', 'pink', 'brown', 'grey'];

export function hashString(input: string): number {
  var hash = 0, i, chr;
  if (input.length === 0) {
      return hash;
  }
  for (i = 0; i < input.length; i++) {
      chr = input.charCodeAt(i);
      // tslint:disable-next-line:no-bitwise
      hash = ((hash << 5) - hash) + chr - 1;
      // tslint:disable-next-line:no-bitwise
      hash |= 0; // Convert to 32bit integer
  }
  return hash;
}

// Enquiry has no priority field, so status drives both the status chip color and the
// card's left border color. The status vocabulary is server-driven (no fixed enum), so
// only the named statuses get their own bucket — anything else falls back to 'active'.
const getStatusBucket = (status: string | null): 'lost' | 'converted' | 'unassigned' | 'inProgress' | 'active' => {
  const lower = (status ?? '').toLowerCase();
  if (lower === 'lost') return 'lost';
  if (lower === 'client created' || lower === 'matter created' || lower === 'client already exists') return 'converted';
  if (lower === 'unassigned') return 'unassigned';
  if (lower === 'assigned' || lower === 'following up' || lower === 'pending matter') return 'inProgress';
  return 'active';
};

const getStatusChipStyle = (status: string | null): { backgroundColor: string; color: string } => {
  const bucket = getStatusBucket(status);
  if (bucket === 'lost') return { backgroundColor: '#FFE0E0', color: '#8a1010' };
  if (bucket === 'converted') return { backgroundColor: '#EAF3DE', color: '#3d6b10' };
  if (bucket === 'unassigned') return { backgroundColor: '#EBEBEB', color: '#555555' };
  if (bucket === 'inProgress') return { backgroundColor: '#FFF3E0', color: '#e65100' };
  return { backgroundColor: '#DDEEFF', color: '#1a5a99' };
};

// Vivid but tasteful palette for the card's left border stripe — brighter than a muted
// palette so stripes are easy to tell apart at a glance, while staying clear of neon and
// near-black shades.
const areaOfLawColorPalette: string[] = [
  '#FB8C00', // orange
  '#8E24AA', // purple
  '#E91E63', // pink
  '#FFB300', // amber
  '#5E35B1', // deep purple
  '#29B6F6', // sky blue
  '#F4511E', // deep orange
  '#C2185B', // maroon pink
  '#FF7043', // coral
  '#AB47BC', // orchid purple
  '#66BB6A', // medium green
  '#F06292', // bright pink
  '#26C6DA', // bright cyan
  '#EF5350', // coral red
  '#FFA726', // light orange
  '#BA68C8', // light purple
];

const areaOfLawBorderColorFallback = '#9e9e9e';

// Assigns each area of law its own color from the palette the first time it's seen, then
// remembers it for the rest of the session — so every card sharing an area of law always
// gets the same stripe color, and no two areas of law share a color while palette slots remain.
const areaOfLawColorAssignments = new Map<string, string>();

const getAreaOfLawBorderColor = (areaOfLaw: string | null): string => {
  const label = (areaOfLaw ?? '').trim();
  if (!label) return areaOfLawBorderColorFallback;

  const existing = areaOfLawColorAssignments.get(label);
  if (existing) return existing;

  const assignedColor = areaOfLawColorPalette[areaOfLawColorAssignments.size % areaOfLawColorPalette.length];
  areaOfLawColorAssignments.set(label, assignedColor);
  return assignedColor;
};

interface BoardCardDraggableProps {
  draggableProvided: DraggableProvided;
  draggableSnapshot: DraggableStateSnapshot;
  // tslint:disable-next-line: no-any
  item: EnquirySummary;
  areaOfLaw: IAutoCompleteItem;
  status: string;
  assignedTo: IAutoCompleteItem;
  dueDate: Date | null;
  groupBy: GroupByType;
  reason: string;
  currencySymbol?: string;
}

interface BoardCardDraggableState {
  groupBy: GroupByType;
  areaOfLaw: IAutoCompleteItem;
  assignedTo: IAutoCompleteItem;
}

const BoardCardDraggableComponent: React.FC<BoardCardDraggableProps> = props => {

  const classes = useStyles();

  const [state, setState] = useState<BoardCardDraggableState>({
    areaOfLaw: props.areaOfLaw,
    assignedTo: props.assignedTo,
    groupBy: props.groupBy
  });

  // const [open, setOpen] = React.useState(false);
  // const anchorRef = React.useRef<HTMLButtonElement>(null);
  // const history = useHistory();

  useEffect(() => {

    // tslint:disable-next-line: no-console
    // console.log('state', state);

    setState((prevState) => {
      return {
        ...prevState,
        groupBy: props.groupBy
      };
    });

  // tslint:disable-next-line: align
  }, [props.groupBy]);

  const [isAreaOfLawSelected, setIsAreaOfLawSelected] = useState(false);
  const [isAssignedToSelected, setIsAssignedToSelected] = useState(false);

  const refAreaOfLaw = React.useRef(null);

  const refAssignedTo = React.useRef(null);

  const taskActionRef = React.useRef<EnquiryTaskActionHandle>(null);

//   const {
//     onFormAction,
//     onEnquiryFormOpen,
//     onFollowUpOpen,
//     onAssignOpen,
//     onCreateClientOpen,
//     onSetGuid
// // tslint:disable-next-line: no-any
// }: any = useContext(EnquirySummaryContext);

  // click outside hide the selector
  useOnClickOutside(refAreaOfLaw, () => setIsAreaOfLawSelected(false));

  // click outside hide the selector
  useOnClickOutside(refAssignedTo, () => setIsAssignedToSelected(false));

  // const handleToggle = () => {
  //   // tslint:disable-next-line: no-shadowed-variable
  //   setOpen(prevOpen => !prevOpen);
  // };

  const avatar = () => {

    let bColor: AvatarColorName = Colors[(state.assignedTo.label === '' || state.assignedTo.label === null) ? 
    0 : (hashString(state.assignedTo.label) % Colors.length)];
    // tslint:disable-next-line: no-any
    let stringSplit = state.assignedTo.label.split(' ').map((n: any) => n[0]).join('');

    let stringInitials = stringSplit.slice(0, 2);

    return (
      <Avatar 
        className={classes.avatar}
        style={{ backgroundColor: bColor ? bColor : 'grey', color: '#fff' }}
      >
        {stringInitials}
      </Avatar>
    );

  };

  // .endOf('day').fromNow()
  const dueDate = () => {
    return `Due ${moment(props.dueDate, 'YYYY-MM-DD HH:mm:ss').fromNow()}`;  
  };

  const handleChangeAreaOfLaw = (data: IAutoCompleteItem, name: string) => {

    setState((prevState) => {
      return {
        ...prevState,
        areaOfLaw: data
      };
    });

  };

  const handleChangeAssignedTo = (data: IAutoCompleteItem, name: string) => {

    setState((prevState) => {
      return {
        ...prevState,
        assignedTo: data
      };
    });

  };

  // const handleClose = (event: React.MouseEvent<EventTarget>) => {
  //   // tslint:disable-next-line
  //   console.log('Close Clicked');
  //   if (anchorRef.current &&
  //       anchorRef.current.contains(event.target as HTMLElement)
  //   ) {
  //       return;
  //   }

  //   setOpen(false);
  // };

  // const handleView = (event: React.MouseEvent<EventTarget>) => {
  //   if (
  //       anchorRef.current &&
  //       anchorRef.current.contains(event.target as HTMLElement)
  //   ) {
  //       return;
  //   }
  //   setOpen(false);
  //   history.push({
  //       pathname: `/enquiriesDetail/${props.item.guid}`
  //   });
  // };

  // const handleListKeyDown = (event: React.KeyboardEvent) => {
  //   if (event.key === 'Tab') {
  //     event.preventDefault();
  //     setOpen(false);
  //   }
  // };

  // const handleFollowUp = (event: React.MouseEvent<EventTarget>) => {
  //   if (anchorRef.current &&
  //       anchorRef.current.contains(event.target as HTMLElement)
  //   ) {
  //       return;
  //   }
  //   onSetGuid(props.item.guid);
  //   setOpen(false);
  //   onFollowUpOpen();
  // };

  // const handleCreateClient = (event: React.MouseEvent<EventTarget>) => {
  //   if (
  //       anchorRef.current &&
  //       anchorRef.current.contains(event.target as HTMLElement)
  //   ) {
  //       return;
  //   }
  //   onSetGuid(props.item.guid);
  //   setOpen(false);
  //   onCreateClientOpen(props.item);
  // };

  // const handleModify = (event: React.MouseEvent<EventTarget>, guid: string) => {
  //   if (anchorRef.current &&
  //       anchorRef.current.contains(event.target as HTMLElement)) {
  //       return;
  //   }

  //   onFormAction('edit', guid);
  //   setOpen(false);
  //   onEnquiryFormOpen();
  // };

  // const handleAssign = (event: React.MouseEvent<EventTarget>) => {
  //   if (
  //       anchorRef.current &&
  //       anchorRef.current.contains(event.target as HTMLElement)
  //   ) {
  //       return;
  //   }

  //   onSetGuid(props.item.guid);
  //   setOpen(false);
  //   onAssignOpen();
  // };

  // const handleEmailAssignee = (event: React.MouseEvent<EventTarget>) => {
  //   if (
  //       anchorRef.current &&
  //       anchorRef.current.contains(event.target as HTMLElement)
  //   ) {
  //       return;
  //   }
  //   setOpen(false);

  //   var mail = document.createElement('a');
  //   // tslint:disable-next-line:max-line-length
  //   mail.href = `mailto:${props.item.assignedToEmailAddress}?subject=
  // RE ${props.item.firstName} ${props.item.lastName}, new business enquiry received 
  // ${moment(props.item.initialEnquiryDate).format('DD MMM YYYY')}`;
  //   mail.click();

  // };

  const isOverDue = () => {
    return moment().diff(props.dueDate, 'days');
  };

  const formattedDate = (date: Date | null, format: string) => {
      if (date) {
        return moment(date).format(format);
      }
      return undefined;
  };

  const onOpenModify = () => {
    taskActionRef.current?.openModify();
  };

  return (
    <div
      className={classes.root}
      key={Math.random()}
      ref={props.draggableProvided.innerRef}
      {...props.draggableProvided.draggableProps}
      {...props.draggableProvided.dragHandleProps}
      style={{
        borderLeft: `4px solid ${getAreaOfLawBorderColor(state.areaOfLaw.label)}`,
        ...props.draggableProvided.draggableProps.style
      }}
    >
      <div className={classes.contentContainer}>
        <div className={classes.optionContainer}>
          <div>
            <div className={classes.cardTypeLabel} onClick={onOpenModify} style={{ cursor: 'pointer' }}>
              {props.item.name}
            </div>
            {props.item.positionAtOrganisation && (
              <div className={classes.cardSubLabel}>{props.item.positionAtOrganisation}</div>
            )}
          </div>
          <div className={classes.option}>
            <EnquiryTaskAction
                ref={taskActionRef}
                enquiryGuid={props.item.guid}
                data={props.item}
            />
          </div>
        </div>
        <div className={classes.content}>
          {props.item.organisation && (<div className="org">{props.item.organisation}</div>)}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
          }}
        >
           <div>
              <Rating 
                value={props.item.enquiryQuality || 0}
                readOnly={true}
                size="small"
              />
           </div>
           <div>
            {`${props.currencySymbol || '$'}${formatNumber(props.item.estimatedFees, 2)}`}
           </div>
        </div>
        <div className={classes.userContainer}>
          {!!(state.groupBy !== GroupByType.areaOfLaw) && (
            <div ref={refAreaOfLaw}>
              {
                isAreaOfLawSelected ? 
                (
                  <AreaOfLawSelector 
                    key={Math.random()}
                    label="Area Of Law"
                    name="areaOfLaw"
                    value={state.areaOfLaw}
                    onSelection={handleChangeAreaOfLaw}
                    disablePortal={true}
                    className={classes.textField}
                  />
                ) 
                : 
                (
                  <div>{state.areaOfLaw.label}</div>
                )
              }
            </div>
          )}
          {!!(state.groupBy === GroupByType.areaOfLaw) && (
            <div ref={refAssignedTo}>
              {
                isAssignedToSelected ? 
                (
                  <UserAutocompleteSelector 
                    value={state.assignedTo}
                    label="Assigned To"
                    name="assignedTo"
                    onSelection={handleChangeAssignedTo}
                    visible={{
                      role: true
                    }}
                  />
                )
                :
                (
                  <>
                    {
                      state.assignedTo.label && state.assignedTo.value && (
                        <div className={classes.userSelector}>
                          {avatar()}
                          <div className="name">{state.assignedTo.label}</div>
                        </div>
                      )
                    }
                  </>
                )
              }
            </div>
          )}
          {!!(state.groupBy === GroupByType.dueDates) && (
            <div className={classes.areaOfLaw}>
              <Chip size="small" label={props.status} style={{ ...getStatusChipStyle(props.status), fontSize: '11px' }} />
            </div>
          )}
        </div>
        <div className={classes.otherDetails}>
          {!!(state.groupBy !== GroupByType.status && state.groupBy !== GroupByType.dueDates) && (
            <div className={classes.areaOfLaw}>
              <Chip size="small" label={props.status} style={{ ...getStatusChipStyle(props.status), fontSize: '11px' }} />
            </div>
          )}
          {!!(state.groupBy === GroupByType.status || state.groupBy === GroupByType.dueDates) && (
            <div ref={refAssignedTo}>
              {
                isAssignedToSelected ? 
                (
                  <UserAutocompleteSelector 
                    value={state.assignedTo}
                    label="Assigned To"
                    name="assignedTo"
                    onSelection={handleChangeAssignedTo}
                    visible={{
                      role: true
                    }}
                  />
                )
                :
                (
                  <>
                    {
                      state.assignedTo.label && state.assignedTo.value && (
                        <div className={classes.userSelector}>
                          {avatar()}
                          <div className="name">{state.assignedTo.label}</div>
                        </div>
                      )
                    }
                  </>
                )
              }
            </div>
          )
          }
          {props.dueDate && props.status !== null && props.status.toLowerCase() !== 'lost' && 
            props.status.toLowerCase() !== 'client created' && props.status.toLowerCase() !== 'matter created' &&
            props.status.toLowerCase() !== 'client already exists' && (
            <div 
              className="session"
              style={{ color: isOverDue() > 0 ? 'red' : '#000'}}
            >
              {dueDate()}
            </div>
          )}
          {props.dueDate && props.status !== null && props.status.toLowerCase() === 'lost' && 
            props.status.toLowerCase() !== 'client created' && props.status.toLowerCase() !== 'matter created' &&
            props.status.toLowerCase() !== 'client already exists' && (
            <div 
              className="session"
              // style={{ color: isOverDue() > 0 ? 'red' : '#000'}}
            >
              {props.reason}
            </div>
          )}
          {props.item.createdDate && props.status !== null && 
           (props.status.toLowerCase() === 'client created' || props.status.toLowerCase() === 'matter created' || props.status.toLowerCase() === 'client already exists') && (
            <div 
              className="session"
              // style={{ color: isOverDue() > 0 ? 'red' : '#000'}}
            >
              {formattedDate(props.item.createdDate, 'DD MMM YYYY')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// draggableProvided/draggableSnapshot are new object references from react-beautiful-dnd on
// every render of the parent Droppable, regardless of whether this card's own data changed —
// comparing them would always report "changed" and defeat memoization entirely, so they're
// excluded here. Drag is disabled on this board (isDragDisabled={true}), so nothing in the
// rendered output actually depends on draggableSnapshot's drag state anyway.
const boardCardDragPropKeys = new Set(['draggableProvided', 'draggableSnapshot']);

const areBoardCardPropsEqual = (prevProps: BoardCardDraggableProps, nextProps: BoardCardDraggableProps): boolean => {
  return (Object.keys(nextProps) as (keyof BoardCardDraggableProps)[])
    .filter(key => !boardCardDragPropKeys.has(key))
    .every(key => prevProps[key] === nextProps[key]);
};

export const BoardCardDraggable = React.memo(BoardCardDraggableComponent, areBoardCardPropsEqual);